import { ChevronRightIcon, MenuIcon } from 'lucide-react';
import React, { createContext, useContext, useMemo } from 'react';
import { Link } from 'react-router';

import { useSidebar } from '@/components/core/sidebar';
import { Skeleton } from '@/components/core/skeleton';
import { DEFAULT_PAGE_TITLE } from '@/constants';
import { cn } from '@/shared/helpers';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import { useNewUiFeatureFlag } from '@/shared/hooks/useNewUiFeatureFlag';

import { Button } from './core/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './core/tooltip';

type HeaderContextData = {
  loading?: boolean;
};

const HeaderContext = createContext<HeaderContextData>({});

export type HeaderProps = React.ComponentProps<'header'> & {
  /**
   * Whether data for the page/header is still loading. This will toggle loading states for the title, description, and
   * current page breadcrumb link.
   */
  loading?: boolean;
};

function HeaderSidebarToggle() {
  const { isMobile, toggleSidebar } = useSidebar();

  if (!isMobile) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggleSidebar}
      className="row-start-1 justify-self-end"
    >
      <MenuIcon />
      <span className="sr-only">Open sidebar</span>
    </Button>
  );
}

export function Header({ title, loading, children, className, ...props }: HeaderProps) {
  const isNewUi = useNewUiFeatureFlag();
  const contextValue = useMemo(() => ({ loading }), [loading]);
  return (
    <HeaderContext.Provider value={contextValue}>
      <header
        className={cn(
          'group grid grid-cols-[auto_1fr_auto] items-center gap-x-1 gap-y-2 border-b px-(--page-inset) py-3',
          'min-h-14 sm:h-14 sm:has-data-[slot=header-description]:h-auto',
          className,
        )}
        {...props}
      >
        {children}
        {isNewUi ? <HeaderSidebarToggle /> : null}
      </header>
    </HeaderContext.Provider>
  );
}

export function HeaderIcon({
  children,
  parentUrl,
  parentTitle,
}: React.PropsWithChildren<{
  parentTitle?: string;
  parentUrl?: string;
}>) {
  return parentUrl ? (
    <div className="row-start-1 mr-1 flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button asChild variant="ghost" size="icon" className="-mx-2">
            <Link to={parentUrl} aria-label={parentTitle ? `Back to ${parentTitle}` : 'Go back'}>
              {children}
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>{parentTitle ? `Back to ${parentTitle}` : 'Go back'}</TooltipContent>
      </Tooltip>
      <ChevronRightIcon className="size-4 text-muted-foreground" />
    </div>
  ) : (
    <span
      className={cn(
        'row-start-1 mr-1 inline-flex size-4 items-center justify-center',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      )}
    >
      {children}
    </span>
  );
}

export function HeaderTitle({
  pageTitle,
  children,
  className,
  ...props
}: React.ComponentProps<'h1'> & {
  /**
   * Override the page title, which is used for the document title and current page breadcrumb. If omitted, this will
   * default to the value of `title`.
   */
  pageTitle?: string;
}) {
  const { loading } = useContext(HeaderContext);
  useDocumentTitle(pageTitle ?? (typeof children === 'string' ? children : DEFAULT_PAGE_TITLE));
  return (
    <h1 {...props} className={cn('row-start-1 min-w-30 text-base font-bold', className)}>
      {loading ? <Skeleton className="h-6 max-w-72" /> : children}
    </h1>
  );
}

export function HeaderDescription({ children, className, ...props }: React.ComponentProps<'div'>) {
  const { loading } = useContext(HeaderContext);
  return (
    <div
      data-slot="header-description"
      className={cn(
        'col-span-full row-start-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground',
        className,
      )}
      {...props}
    >
      {loading ? <Skeleton className="h-5 w-64" /> : children}
    </div>
  );
}

export function HeaderActions({ children, className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'row-start-1 flex flex-wrap items-center justify-end gap-2 self-center',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}
